import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { DeletePayrollResultDocument, PayrollResultFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Item from './_item'

interface ManagePayrollResultsModalProps {
  payrollResults: PayrollResultFragment[]
  onClose?: () => void
  onUpdate?: () => void
  onImportNewResultClick?: () => void
  isCyclesManagementFeatureEnabled: boolean
  canImportCompensations: boolean
}

const ManagePayrollResultsModal = ({
  payrollResults,
  onClose,
  onUpdate,
  onImportNewResultClick,
  isCyclesManagementFeatureEnabled,
  canImportCompensations,
}: ManagePayrollResultsModalProps) => {
  const [deletePayrollResult] = useMutation(DeletePayrollResultDocument, {
    extractorFn: (data) => data.payrollMutations.deletePayrollResult,
  })

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleImportClick = () => {
    if (onImportNewResultClick) {
      onImportNewResultClick()
    }
  }

  const handleRemoveResult = async (result: PayrollResultFragment) => {
    const response = await deletePayrollResult({
      variables: {
        id: result.id,
      },
      onCompleted: () => {
        track(trackingEvents.compensations.removePayrollResults, {
          payroll_result_id: result.id,
        })
      },
    })

    const handled = await handleResult(response, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.delete_success'
        ),
      })
      onUpdate?.()
      handleClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  return (
    <Modal
      illustration="table-file"
      title={i18n.t('compensations.PayrollRunCompensationsList.manage_payroll_results.modal.title')}
      onClose={handleClose}
      footer={
        <Button type="primary" onClick={handleClose}>
          {i18n.t('common.close')}
        </Button>
      }
    >
      <Box gap="s16" flexGrow>
        {!isCyclesManagementFeatureEnabled && canImportCompensations && (
          <Button type="secondary" iconName="upload" onClick={handleImportClick}>
            {i18n.t(
              'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_new_results'
            )}
          </Button>
        )}
        {payrollResults.length > 0 ? (
          payrollResults.map((result) => (
            <Item key={result.id} result={result} onRemove={handleRemoveResult} />
          ))
        ) : (
          <Box padding="s16" background="grey100">
            <Text.Normal>
              {i18n.t(
                'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.no_results'
              )}
            </Text.Normal>
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default ManagePayrollResultsModal
